<template>
  <section class="fcp-col" :class="{ 'fcp-col--mobile': mobile }">
    <div v-if="loadingConv" class="fcp-loading"><span class="fcp-spinner"></span></div>

    <template v-else-if="conv">
      <!-- Cabeçalho -->
      <div class="fcp-head">
        <button v-if="mobile" class="fcp-icon-btn fcp-back" @click="$emit('close')" title="Voltar">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
        </button>
        <div class="fcp-av-wrap">
          <div class="fcp-av" :style="{ background: avatarColor(conv.contactName) }">
            <img v-if="conv.contactAvatarUrl" :src="toMediaUrl(conv.contactAvatarUrl)!" :alt="conv.contactName" @error="(e) => (e.target as HTMLImageElement).style.display='none'" />
            <span v-else>{{ initials(conv.contactName) }}</span>
          </div>
          <span class="fcp-status-dot" :class="statusClass(conv.status)"></span>
        </div>
        <LinkusCallButton variant="light" :conversation-id="conv.id" :phone="conv.contactPhone" :contact-name="conv.contactName" />
        <div class="fcp-hinfo">
          <div class="fcp-hname-row">
            <span class="fcp-hname">{{ conv.contactName }}</span>
            <AgendorMenu :contact-id="conv.contactId" :conversation-id="conv.id" :conversation-agent-id="conv.agentId" :contact-name="conv.contactName" variant="light" />
            <span v-if="conv.contactPhone" class="fcp-hphone">
              {{ fmtPhone(conv.contactPhone) }}
              <button class="fcp-phone-copy" :class="{ copied: phoneCopied }" @click="copyPhone" :title="phoneCopied ? 'Copiado!' : 'Copiar número'">
                <svg v-if="phoneCopied" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                <svg v-else width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
              </button>
            </span>
          </div>
          <div class="fcp-hsub">
            <span v-if="conv.teamName">{{ conv.teamName }} &middot; </span>
            <span v-if="conv.flowName" class="fcp-tag fcp-tag--fluxo">{{ conv.flowName }}</span>
            <BotVersionTag v-if="conv.bot" :bot="conv.bot" />
            <span class="fcp-tag" :class="'fcp-tag--' + statusClass(conv.status)">{{ STATUS_LABEL[conv.status] }}</span>
            <span v-if="slaBadge" class="fcp-tag fcp-tag--sla" :class="slaBadge.status">
              <strong>{{ slaBadge.time }}</strong> {{ slaBadge.label }}
            </span>
          </div>
        </div>

        <div v-if="can('messages.changeStatus')" class="fcp-dd-wrap" @click.stop>
          <button class="fcp-icon-btn" :title="`Status: ${STATUS_LABEL[conv.status]}`" @click="statusMenuOpen = !statusMenuOpen">
            <span class="fcp-s-dot" :class="statusClass(conv.status)"></span>
          </button>
          <div v-if="statusMenuOpen" class="fcp-dd-menu">
            <button v-if="conv.status !== 0" class="fcp-sopt" @click="doChangeStatus('Open')"><span class="fcp-s-dot open"></span> Abrir</button>
            <button v-if="conv.status !== 2" class="fcp-sopt" @click="doResolve"><span class="fcp-s-dot resolved"></span> Resolver</button>
            <button v-if="conv.status !== 1" class="fcp-sopt" @click="doChangeStatus('Pending')"><span class="fcp-s-dot pending"></span> Pendente</button>
          </div>
        </div>
        <button v-if="can('messages.runMacro')" class="fcp-icon-btn" :class="{ active: macroPanelOpen }" @click="toggleMacroPanel" :title="macroPanelOpen ? 'Voltar à conversa' : 'Executar macro'">
          <svg v-if="!macroPanelOpen" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polygon points="5 3 19 12 5 21 5 3"/></svg>
          <svg v-else width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
        </button>
        <button v-if="!mobile" class="fcp-icon-btn" @click="$emit('close')" title="Fechar conversa">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
        </button>
      </div>

      <!-- Atribuição + etiquetas -->
      <div class="fcp-toolbar">
        <div v-if="can('messages.assignAgent')" class="fcp-dd-wrap" @click.stop>
          <span v-if="conv.agentName" class="fcp-pill fcp-pill--sel" @click.stop="agentMenuOpen = !agentMenuOpen">{{ conv.agentName }}</span>
          <button v-else class="fcp-pill fcp-pill--add" @click.stop="agentMenuOpen = !agentMenuOpen">+ agente</button>
          <div v-if="agentMenuOpen" class="fcp-dd-menu fcp-dd-menu--wide">
            <input v-model="agentSearch" class="fcp-dd-search" placeholder="Buscar agente…" @click.stop @keydown.stop />
            <div v-if="agentLoading" class="fcp-dd-loading"><span class="fcp-spinner-sm"></span></div>
            <div v-else class="fcp-dd-list">
              <button v-for="a in filteredAgents" :key="a.id" class="fcp-dd-opt" :class="{ active: conv.agentId === a.id }" @click.stop="doAssignAgent(a.id)">
                {{ a.firstName }} {{ a.lastName }}
              </button>
            </div>
          </div>
        </div>

        <div v-if="can('messages.assignTeam')" class="fcp-dd-wrap" @click.stop>
          <span v-if="conv.teamName" class="fcp-pill fcp-pill--sel" @click.stop="teamMenuOpen = !teamMenuOpen">{{ conv.teamName }}</span>
          <button v-else class="fcp-pill fcp-pill--add" @click.stop="teamMenuOpen = !teamMenuOpen">+ time</button>
          <div v-if="teamMenuOpen" class="fcp-dd-menu fcp-dd-menu--wide">
            <input v-model="teamSearch" class="fcp-dd-search" placeholder="Buscar time…" @click.stop @keydown.stop />
            <div v-if="teamsLoading" class="fcp-dd-loading"><span class="fcp-spinner-sm"></span></div>
            <div v-else class="fcp-dd-list">
              <button v-for="t in filteredTeams" :key="t.id" class="fcp-dd-opt" :class="{ active: conv.teamId === t.id }" @click.stop="doAssignTeam(t.id)">{{ t.name }}</button>
            </div>
          </div>
        </div>

        <span v-for="name in conv.tagNames" :key="name" class="fcp-pill fcp-pill--tag">
          {{ name }}
          <button v-if="!isTagProtected(name)" class="fcp-pill-x" :disabled="updatingTags" @click.stop="toggleTag(name)">×</button>
        </span>
        <div v-if="can('messages.manageTags')" class="fcp-dd-wrap" @click.stop>
          <button class="fcp-pill fcp-pill--add" :disabled="updatingTags" @click.stop="tagsMenuOpen = !tagsMenuOpen">+ etiqueta</button>
          <div v-if="tagsMenuOpen" class="fcp-dd-menu fcp-dd-menu--wide">
            <label v-for="tag in allTags" :key="tag.id" class="fcp-dd-opt fcp-dd-opt--check" @click.prevent="(conv.tagNames.includes(tag.name) && tag.isProtected) || toggleTag(tag.name)">
              <span class="fcp-lbl-dot" :style="{ background: tag.color }"></span>
              {{ tag.name }}
              <svg v-if="conv.tagNames.includes(tag.name)" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" style="margin-left:auto;color:#22c55e"><polyline points="20 6 9 17 4 12"/></svg>
            </label>
          </div>
        </div>
      </div>

      <div v-if="windowClosed" class="fcp-window-banner">
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
        <span>Você só pode responder usando um template — janela de 24h do WhatsApp encerrada</span>
      </div>

      <!-- Painel de macros -->
      <div v-if="macroPanelOpen" class="fcp-macro-panel">
        <template v-if="macroFormTarget && !macroRunning && !macroRunDone">
          <div class="fcp-macro-head">
            <button class="fcp-macro-back" @click="macroFormTarget = null">‹ Voltar</button>
            <span class="fcp-macro-title">{{ macroFormTarget.name }}</span>
          </div>
          <div class="fcp-macro-form">
            <p class="fcp-mform-hint">Preencha os campos antes de executar</p>
            <div v-for="field in macroFormFields" :key="field.key" class="fcp-mform-field">
              <label class="fcp-mform-label">{{ field.label || field.key }}</label>
              <input class="fcp-mform-input" v-model="macroFormValues[field.key]" :placeholder="field.label || field.key" />
            </div>
            <button class="fcp-btn-primary" @click="submitMacroForm">Executar</button>
          </div>
        </template>
        <template v-else-if="!macroRunning && !macroRunDone">
          <div class="fcp-macro-head">
            <input v-model="macroSearch" class="fcp-macro-search" placeholder="Buscar macro…" />
            <span class="fcp-macro-count">{{ filteredMacros.length }}</span>
          </div>
          <div v-if="macrosLoading" class="fcp-dd-loading"><span class="fcp-spinner-sm"></span></div>
          <div v-else class="fcp-macro-list">
            <button v-for="m in filteredMacros" :key="m.id" class="fcp-macro-item" @click="runMacroRow(m)">
              <span class="fcp-macro-item-name">{{ m.name }}</span>
              <span class="fcp-macro-item-sub">{{ m.actions.length }} ação{{ m.actions.length !== 1 ? 'ões' : '' }}</span>
            </button>
            <div v-if="!filteredMacros.length" class="fcp-macro-empty">Nenhuma macro encontrada</div>
          </div>
        </template>
        <template v-else>
          <div class="fcp-macro-head">
            <button v-if="macroRunDone" class="fcp-macro-back" @click="resetMacroRun">‹ Voltar</button>
            <span class="fcp-macro-title">{{ macroRunningObj?.name }}</span>
            <span v-if="macroRunning" class="fcp-spinner-sm"></span>
          </div>
          <div v-if="macroRunDone" class="fcp-macro-summary" :class="macroResult?.success ? 'ok' : 'warn'">
            {{ macroResult?.success ? `Concluído — ${macroResult.actionsApplied} ação(ões) aplicada(s)` : `Falhou: ${macroResult?.failureReason}` }}
          </div>
        </template>
      </div>

      <!-- Mensagens -->
      <div v-show="!macroPanelOpen" class="fcp-msgs" ref="msgsEl" @scroll="onMsgsScroll">
        <div v-if="loadingMsgs" class="fcp-msgs-loading"><span class="fcp-spinner"></span></div>
        <template v-else>
          <div v-if="loadingMore" class="fcp-load-more"><span class="fcp-spinner-sm"></span></div>
          <div v-else-if="noMoreMsgs" class="fcp-no-more">início da conversa</div>
          <div v-for="msg in processedMsgs" :key="msg.id" class="fcp-msg-row" :class="{ out: msg.senderType === 'Agent', inn: msg.senderType === 'Contact' || msg.senderType === 'Bot', act: msg.senderType === 'System' }">
            <div v-if="msg.senderType === 'System'" class="fcp-msg-act">{{ msg.body }}</div>
            <template v-else>
              <div v-if="msg.showAvatar" class="fcp-msg-av" :style="{ background: msgSender(msg).color }" :title="msgSender(msg).name">
                <img v-if="msgSender(msg).avatarUrl" :src="msgSender(msg).avatarUrl!" :alt="msgSender(msg).name" @error="(e) => (e.target as HTMLImageElement).style.display = 'none'" />
                <span v-else>{{ msgSender(msg).initials }}</span>
              </div>
              <div v-else class="fcp-msg-av-spacer"></div>
              <div class="fcp-bubble" :class="{ priv: msg.isPrivateNote }">
                <span v-if="msg.isPrivateNote" class="fcp-priv-tag">Nota interna</span>
                <span v-if="msg.body" class="fcp-bubble-text" v-html="whatsAppToHtml(msg.body)"></span>
                <div v-if="msg.mediaUrl" class="fcp-att">
                  <a v-if="isImageMime(msg.mediaMimeType)" :href="toMediaUrl(msg.mediaUrl)!" target="_blank" rel="noopener">
                    <img :src="toMediaUrl(msg.mediaUrl)!" class="fcp-att-img" alt="imagem" loading="lazy" />
                  </a>
                  <audio v-else-if="isAudioMime(msg.mediaMimeType)" :src="toMediaUrl(msg.mediaUrl)!" controls class="fcp-att-audio"></audio>
                  <video v-else-if="isVideoMime(msg.mediaMimeType)" :src="toMediaUrl(msg.mediaUrl)!" controls preload="metadata" class="fcp-att-video"></video>
                  <a v-else :href="toMediaUrl(msg.mediaUrl)!" target="_blank" rel="noopener" class="fcp-att-file">📎 Anexo</a>
                </div>
                <span class="fcp-bubble-ts"><span v-if="msg.origin === 'copilot'" class="copilot-tag" title="Enviada automaticamente pelo copiloto de IA">IA</span>{{ fmtTs(msg.createdAt) }}<MessageTicks v-if="msg.senderType !== 'Contact' && !msg.isPrivateNote" :status="msg.deliveryStatus" :error="msg.deliveryError" /></span>
              </div>
            </template>
          </div>
        </template>
      </div>

      <div v-if="files.length && !macroPanelOpen && !finished" class="fcp-files">
        <div v-for="(f, i) in files" :key="i" class="fcp-file-pill">
          <span class="fcp-file-name">{{ f.name }}</span>
          <button class="fcp-file-rm" @click="files.splice(i, 1)">×</button>
        </div>
      </div>

      <CopilotBar v-if="!finished" ref="copilotBar" :conversation-id="conversationId" :draft="draft" @fill="fillFromCopilot" />
      <!-- Resolvida ou fechada: atendimento encerrado, nada sai (o backend também recusa) até reabrir. -->
      <div v-if="finished && !macroPanelOpen" class="fcp-finished" role="status">
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
        <span>Conversa {{ conv.status === 3 ? 'fechada' : 'resolvida' }}: não dá pra enviar mensagem, anexo nem template.</span>
        <button v-if="can('messages.changeStatus')" type="button" class="fcp-finished-btn" @click="doChangeStatus('Open')">Reabrir</button>
      </div>
      <div v-else-if="!macroPanelOpen" class="fcp-composer" :class="{ priv: notaInterna }">
        <label class="fcp-attach-btn" :class="{ disabled: windowClosed || notaInterna }">
          <input type="file" multiple hidden :disabled="windowClosed || notaInterna" @change="onFilePick" />
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/></svg>
        </label>
        <button v-if="can('messages.privateNote')" class="fcp-priv-btn" :class="{ active: notaInterna }" :disabled="windowClosed" @click="notaInterna = !notaInterna" title="Nota interna">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
        </button>
        <textarea
          ref="taEl" v-model="draft" class="fcp-ta" rows="1" :disabled="windowClosed"
          :placeholder="notaInterna ? 'Nota interna… (Enter envia)' : (windowClosed ? 'Envie um template para responder…' : 'Mensagem… (Enter envia)')"
          @keydown.enter.exact.prevent="sendMsg" @input="growTa(); presence.typing(props.conversationId, notaInterna)"
        ></textarea>
        <button v-if="!notaInterna && windowClosed && can('messages.sendTemplate')" class="fcp-tpl-btn" @click="openTplModal" title="Enviar template WhatsApp">Template</button>
        <button v-else class="fcp-send-btn" :disabled="!canSend || sending" @click="sendMsg">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
        </button>
      </div>
    </template>

    <div v-else class="fcp-empty">
      <p>Conversa não encontrada.</p>
      <button v-if="mobile" class="fcp-btn-primary" @click="$emit('close')">Voltar</button>
    </div>

    <!-- Modal de template -->
    <Teleport to="body">
      <div v-if="tplOpen" class="fcp-tpl-overlay" @mousedown.self="closeTplModal">
        <div class="fcp-tpl-dialog">
          <div class="fcp-tpl-head">
            <span>Enviar Template WhatsApp → {{ conv?.contactName }}</span>
            <button class="fcp-icon-btn" @click="closeTplModal">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M18 6 6 18M6 6l12 12"/></svg>
            </button>
          </div>
          <div class="fcp-tpl-body">
            <div class="fcp-tpl-list-col">
              <div v-if="tplsLoading" class="fcp-dd-loading"><span class="fcp-spinner-sm"></span></div>
              <div v-else-if="!tplsApproved.length" class="fcp-macro-empty">Nenhum template aprovado</div>
              <button v-for="t in tplsApproved" :key="t.id" class="fcp-tpl-item" :class="{ active: tplSel?.id === t.id }" @click="selectTpl(t)">
                {{ t.name.replace(/_/g, ' ') }}
              </button>
            </div>
            <div class="fcp-tpl-config-col">
              <template v-if="tplSel">
                <div class="fcp-tpl-preview" v-html="tplPreviewHtml"></div>
                <div v-for="n in tplVarNums" :key="n" class="fcp-mform-field">
                  <label class="fcp-mform-label">Variável &#123;&#123;{{ n }}&#125;&#125;</label>
                  <span v-if="tplVarMapping[n]" class="fcp-mform-mapped">Preenchido automaticamente: {{ mappedLabel(n) }}</span>
                  <input v-else class="fcp-mform-input" :value="tplVars[n] || ''" @input="setTplVar(n, ($event.target as HTMLInputElement).value)" />
                </div>
                <button class="fcp-btn-primary" :disabled="tplSending" @click="sendTpl">
                  {{ tplSending ? 'Enviando…' : `Enviar para ${conv?.contactName}` }}
                </button>
              </template>
              <p v-else class="fcp-macro-empty">Selecione um template ao lado</p>
            </div>
          </div>
        </div>
      </div>
    </Teleport>
  </section>
</template>

<script setup lang="ts">
// Chat embutido do Modo Foco — construído em paralelo ao ConvDrawer.vue (não reaproveitado
// dele: ConvDrawer é um singleton global sem props, montado uma vez em App.vue e controlado
// por sinais globais; embuti-lo lado a lado da lista do Foco exigiria refatorar um
// componente de 3000+ linhas usado pelo app inteiro — risco/escopo maiores do que faz
// sentido aqui. Reaproveita, sim, os composables/serviços já extraídos e comprovados contra
// o backend real (useMessageCacheCp, useMessageWindowCp, useWhatsappTemplatesCp,
// useConversationRealtime, wootdashConversations/Agents/Teams/Tags/Attachments/Macros)
// — só a orquestração de UI é duplicada, não a integração com o backend.
//
// Deliberadamente fora de escopo (documentado, não esquecido): busca dentro da conversa,
// emoji picker, gravação de áudio/vídeo, histórico de contato, exportar conversa,
// prioridade (não existe no domínio do WootDash) e adiar com prazo (idem — só
// Aberta/Pendente existem aqui).
import { ref, computed, watch, onMounted, onUnmounted, nextTick } from 'vue'
import { useStore } from 'vuex'
import Swal from 'sweetalert2'
import { avatarColor, initials } from '@/composables/useHelpers'
import { fmtPhone } from '@/utils/contactsFormat'
import { toMediaUrl } from '@/utils/media'
import { whatsAppToHtml } from '@/utils/whatsAppText'
import { usePermission } from '@/composables/usePermission'
import { useMessageCacheCp } from '@/composables/useMessageCacheCp'
import { useMessageWindowCp } from '@/composables/useMessageWindowCp'
import { useWhatsappTemplatesCp } from '@/composables/useWhatsappTemplatesCp'
import { onConversationMessage, onConversationStatus, onRealtimeRecovered, type MessageStatusNotification } from '@/composables/useConversationRealtime'
import { useWhatsAppPresence } from '@/composables/useWhatsAppPresence'
import MessageTicks from '@/components/dashboard/MessageTicks.vue'
import CopilotBar from '@/components/dashboard/CopilotBar.vue'
import LinkusCallButton from '@/components/dashboard/LinkusCallButton.vue'
import AgendorMenu from '@/components/agendor/AgendorMenu.vue'
import BotVersionTag from '@/components/dashboard/BotVersionTag.vue'
import { useBotVersionRefresh } from '@/composables/useBotVersionRefresh'
import { playNotificationSound } from '@/composables/useNotificationSound'
import { loadMessageAvatars, senderOf, sameSender } from '@/composables/useMessageAvatars'
import {
  getConversation, getMessages, sendMessage, assignConversation, assignConversationTeam,
  resolveConversation, changeConversationStatus, addConversationTag, removeConversationTag, STATUS_LABEL,
  type ConversationDetail,
} from '@/services/wootdashConversations'
import { listAgents, type CpAgent } from '@/services/wootdashAgents'
import { listTeams, type CpTeam } from '@/services/wootdashTeams'
import { listTags, type CpTag } from '@/services/wootdashTags'
import { uploadAttachment, MAX_ATTACHMENT_BYTES } from '@/services/wootdashAttachments'
import { listMacros, executeMacro, type CpMacro } from '@/services/wootdashMacros'
import type { ChatMessageCp, ProcessedMsgCp } from '@/types/chatCp'

// mobile: tela cheia da MobileView (seta de voltar no lugar do X, layout estreito).
const props = defineProps<{ conversationId: string; mobile?: boolean }>()
defineEmits<{ close: [] }>()

const store = useStore()
const token = computed(() => store.getters.authUser?.wootdash_token ?? '')
const { can } = usePermission()

const loadingConv = ref(false)
const conv = ref<ConversationDetail | null>(null)
useBotVersionRefresh(conv, () => token.value) // etiqueta da versão do bot sem polling
const { msgCache } = useMessageCacheCp()
const loadingMsgs = ref(false)
const loadingMore = ref(false)
const noMoreMsgs = ref(false)
const draft = ref('')
const files = ref<File[]>([])
const notaInterna = ref(false)
/** Resolvida (2) ou fechada (3): o compositor some e o envio é recusado — reabrir libera. */
const finished = computed(() => conv.value?.status === 2 || conv.value?.status === 3)
const presence = useWhatsAppPresence(token)
const msgsEl = ref<HTMLElement | null>(null)
const taEl = ref<HTMLTextAreaElement | null>(null)
const phoneCopied = ref(false)
const statusMenuOpen = ref(false)
const nowSec = ref(Math.floor(Date.now() / 1000))

function statusClass(status: number): string {
  return ({ 0: 'open', 1: 'pending', 2: 'resolved', 3: 'closed' } as Record<number, string>)[status] ?? 'open'
}

const slaBadge = computed<{ label: string; time: string; status: 'breached' | 'atRisk' } | null>(() => {
  const c = conv.value
  if (!c || !c.slaDueAt) return null
  const due = new Date(c.slaDueAt).getTime() / 1000
  const remaining = due - nowSec.value
  const breached = !!c.slaBreachedAt || remaining <= 0
  if (!breached && remaining > 15 * 60) return null
  const abs = Math.abs(Math.floor(remaining))
  const h = Math.floor(abs / 3600), m = Math.floor((abs % 3600) / 60), s = abs % 60
  const timeStr = (remaining <= 0 ? '+' : '') + (h > 0 ? h + 'h ' : '') + (m > 0 ? m + 'min' : s + 's')
  return { label: c.slaPhase || 'SLA', time: timeStr, status: breached ? 'breached' : 'atRisk' }
})

const processedMsgs = computed<ProcessedMsgCp[]>(() => {
  const msgs = msgCache.value[props.conversationId] || []
  return msgs.map((msg, i) => {
    const next = msgs[i + 1]
    return { ...msg, showAvatar: !sameSender(next, msg) }
  })
})
const canSend = computed(() => draft.value.trim().length > 0 || files.value.length > 0)
const { isWindowClosed: windowClosed } = useMessageWindowCp(processedMsgs, nowSec)
watch(windowClosed, closed => { if (closed) notaInterna.value = false })

function isImageMime(m: string | null) { return !!m && m.startsWith('image/') }
function isAudioMime(m: string | null) { return !!m && m.startsWith('audio/') }
function isVideoMime(m: string | null) { return !!m && m.startsWith('video/') }
/** Foto + nome de quem mandou (atendente pelo senderId, agente de IA, bot, contato) — ver useMessageAvatars. */
function msgSender(msg: ChatMessageCp) {
  return senderOf(msg, { name: conv.value?.contactName, avatarUrl: conv.value?.contactAvatarUrl })
}
function fmtTs(iso: string) {
  const d = new Date(iso)
  return `${d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })} ${d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`
}
async function copyPhone() {
  if (!conv.value?.contactPhone) return
  await navigator.clipboard.writeText(conv.value.contactPhone)
  phoneCopied.value = true
  setTimeout(() => { phoneCopied.value = false }, 2000)
}

/* ── Carregar conversa + mensagens ── */
const PAGE_SIZE = 50

function mergeMsgs(existing: ChatMessageCp[], incoming: ChatMessageCp[]): ChatMessageCp[] {
  const byId = new Map(existing.map(m => [m.id, m]))
  incoming.forEach(m => byId.set(m.id, m))
  return [...byId.values()].sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime())
}
function isNearBottom(): boolean {
  const el = msgsEl.value
  if (!el) return true
  return el.scrollHeight - el.scrollTop - el.clientHeight < 120
}
function scrollBottom() { if (msgsEl.value) msgsEl.value.scrollTop = msgsEl.value.scrollHeight }

async function fetchMsgs(convId: string, silent = true) {
  if (!silent) loadingMsgs.value = true
  noMoreMsgs.value = false
  const wasNearBottom = isNearBottom()
  try {
    const messages = await getMessages(token.value, convId, { limit: PAGE_SIZE })
    const prevMsgs = msgCache.value[convId]
    if (prevMsgs && messages.some(m => m.senderType === 'Contact' && !prevMsgs.some(p => p.id === m.id))) playNotificationSound()
    msgCache.value = { ...msgCache.value, [convId]: silent && prevMsgs ? mergeMsgs(prevMsgs, messages) : messages }
    noMoreMsgs.value = messages.length < PAGE_SIZE
  } finally {
    loadingMsgs.value = false
    if (convId === props.conversationId && (!silent || wasNearBottom)) { await nextTick(); scrollBottom() }
  }
}

async function loadMoreMsgs() {
  const convId = props.conversationId
  if (loadingMore.value || noMoreMsgs.value) return
  const current = msgCache.value[convId] ?? []
  if (!current.length) return
  loadingMore.value = true
  const prevHeight = msgsEl.value?.scrollHeight ?? 0
  try {
    const older = await getMessages(token.value, convId, { before: current[0].createdAt, limit: PAGE_SIZE })
    if (!older.length) { noMoreMsgs.value = true; return }
    msgCache.value = { ...msgCache.value, [convId]: mergeMsgs(older, current) }
    await nextTick()
    if (msgsEl.value) msgsEl.value.scrollTop = msgsEl.value.scrollHeight - prevHeight
  } finally {
    loadingMore.value = false
  }
}
function onMsgsScroll() { if (msgsEl.value && msgsEl.value.scrollTop < 60) loadMoreMsgs() }

async function loadConversation(id: string) {
  loadingConv.value = true
  void loadMessageAvatars(token.value)
  try {
    conv.value = await getConversation(token.value, id)
    if (props.conversationId !== id) return
    const hadCache = !!msgCache.value[id]
    await fetchMsgs(id, hadCache)
  } finally {
    loadingConv.value = false
  }
}

let msgRealtimeUnsub: (() => void) | null = null

function onRealtimeMsg(convId: string, message: ChatMessageCp) {
  const prevMsgs = msgCache.value[convId]
  if (!prevMsgs) { fetchMsgs(convId, true); return }
  const isNewIncoming = message.senderType === 'Contact' && !prevMsgs.some(m => m.id === message.id)
  const wasNearBottom = isNearBottom()
  msgCache.value = { ...msgCache.value, [convId]: mergeMsgs(prevMsgs, [message]) }
  if (isNewIncoming) playNotificationSound()
  if (isNewIncoming && convId === props.conversationId) presence.seen(convId)
  if (convId === props.conversationId && wasNearBottom) nextTick(scrollBottom)
}

/* ── Enviar ── */
const sending = ref(false)
// Copiloto: a sugestão entra no campo (o atendente revisa e envia); avisamos o envio pra medir o aproveitamento.
const copilotBar = ref<InstanceType<typeof CopilotBar> | null>(null)
function fillFromCopilot(text: string) {
  draft.value = text
  nextTick(() => growTa())
}

async function sendMsg() {
  const convId = props.conversationId
  if (sending.value || finished.value || (!draft.value.trim() && !files.value.length)) return
  sending.value = true
  const text = draft.value.trim()
  const pickedFiles = [...files.value]
  const isPrivate = notaInterna.value
  draft.value = ''
  files.value = []
  nextTick(() => { growTa(); taEl.value?.focus() })
  try {
    if (text) {
      const ok = await sendMessage(token.value, convId, text, isPrivate)
      if (!ok) throw new Error('Falha ao enviar texto')
      if (!isPrivate) copilotBar.value?.sent(text)
    }
    presence.sent()
    const failed: string[] = []
    for (const file of pickedFiles) {
      const uploaded = file.size > MAX_ATTACHMENT_BYTES ? null : await uploadAttachment(token.value, file)
      if (!uploaded || !(await sendMessage(token.value, convId, null, isPrivate, uploaded))) failed.push(file.name)
    }
    if (failed.length) {
      Swal.fire({ title: 'Anexo não enviado', text: `Não foi possível enviar: ${failed.join(', ')}. O limite é 25 MB por arquivo.`, icon: 'warning' })
    }
    await fetchMsgs(convId, true)
  } catch {
    Swal.fire({ title: 'Erro ao enviar', text: 'Não foi possível enviar a mensagem.', icon: 'error' })
  } finally {
    sending.value = false
  }
}
function onFilePick(e: Event) {
  const input = e.target as HTMLInputElement
  if (input.files) { files.value.push(...Array.from(input.files)); input.value = '' }
}
function growTa() {
  const el = taEl.value
  if (!el) return
  el.style.height = 'auto'
  el.style.height = Math.min(el.scrollHeight, 120) + 'px'
}

/* ── Status ── */
async function doChangeStatus(status: 'Open' | 'Pending') {
  if (!conv.value) return
  statusMenuOpen.value = false
  const ok = await changeConversationStatus(token.value, conv.value.id, status)
  if (ok) conv.value = { ...conv.value, status: status === 'Open' ? 0 : 1 }
}
async function doResolve() {
  if (!conv.value) return
  statusMenuOpen.value = false
  const result = await Swal.fire({
    title: 'Resolver esta conversa?', icon: 'warning', showCancelButton: true,
    confirmButtonColor: '#BC0C24', cancelButtonColor: '#6b7280', confirmButtonText: 'Sim, resolver', cancelButtonText: 'Cancelar', reverseButtons: true,
  })
  if (!result.isConfirmed) return
  const ok = await resolveConversation(token.value, conv.value.id)
  if (ok) conv.value = { ...conv.value, status: 2 }
}

/* ── Agente ── */
const agentMenuOpen = ref(false)
const agentLoading = ref(false)
const allAgents = ref<CpAgent[]>([])
const agentSearch = ref('')
let agentsFetched = false
const filteredAgents = computed(() => {
  const q = agentSearch.value.toLowerCase().trim()
  // Modo observador nunca recebe conversa: fica fora da lista de atribuição.
  const assignable = allAgents.value.filter(a => !a.isObserver)
  return q ? assignable.filter(a => `${a.firstName} ${a.lastName}`.toLowerCase().includes(q)) : assignable
})
async function fetchAgents() {
  if (agentLoading.value || agentsFetched) return
  agentsFetched = true
  agentLoading.value = true
  try { allAgents.value = (await listAgents(token.value)).agents } catch { agentsFetched = false } finally { agentLoading.value = false }
}
watch(agentMenuOpen, open => { if (open) fetchAgents() })
async function doAssignAgent(agentId: string) {
  if (!conv.value) return
  agentMenuOpen.value = false
  const agent = allAgents.value.find(a => a.id === agentId)
  const ok = await assignConversation(token.value, conv.value.id, agentId)
  if (ok && agent) conv.value = { ...conv.value, agentId: agent.id, agentName: `${agent.firstName} ${agent.lastName}` }
}

/* ── Time ── */
const teamMenuOpen = ref(false)
const teamsLoading = ref(false)
const allTeams = ref<CpTeam[]>([])
const teamSearch = ref('')
let teamsFetched = false
const filteredTeams = computed(() => {
  const q = teamSearch.value.toLowerCase().trim()
  return q ? allTeams.value.filter(t => t.name.toLowerCase().includes(q)) : allTeams.value
})
async function fetchTeamsOnce() {
  if (teamsFetched) return
  teamsFetched = true
  teamsLoading.value = true
  try { allTeams.value = await listTeams(token.value) } finally { teamsLoading.value = false }
}
watch(teamMenuOpen, open => { if (open) fetchTeamsOnce() })
async function doAssignTeam(teamId: string) {
  if (!conv.value) return
  teamMenuOpen.value = false
  const team = allTeams.value.find(t => t.id === teamId)
  const ok = await assignConversationTeam(token.value, conv.value.id, teamId)
  if (ok && team) conv.value = { ...conv.value, teamId: team.id, teamName: team.name }
}

/* ── Etiquetas ── */
const tagsMenuOpen = ref(false)
const updatingTags = ref(false)
const allTags = ref<CpTag[]>([])
async function fetchTagsOnce() { if (!allTags.value.length) allTags.value = await listTags(token.value) }
watch(tagsMenuOpen, open => { if (open) fetchTagsOnce() })
function isTagProtected(name: string) { return allTags.value.find(t => t.name === name)?.isProtected ?? false }
async function toggleTag(name: string) {
  if (!conv.value || updatingTags.value) return
  const c = conv.value
  const tag = allTags.value.find(t => t.name === name)
  if (!tag) return
  const has = c.tagNames.includes(name)
  updatingTags.value = true
  try {
    const ok = has ? await removeConversationTag(token.value, c.id, tag.id) : await addConversationTag(token.value, c.id, tag.id)
    if (ok) conv.value = { ...c, tagNames: has ? c.tagNames.filter(n => n !== name) : [...c.tagNames, name] }
  } finally {
    updatingTags.value = false
  }
}

/* ── Macros ── */
const macroPanelOpen = ref(false)
const macrosList = ref<CpMacro[]>([])
const macrosLoading = ref(false)
const macroSearch = ref('')
const macroRunning = ref(false)
const macroRunDone = ref(false)
const macroRunningObj = ref<CpMacro | null>(null)
const macroResult = ref<{ success: boolean; failureReason: string; actionsApplied: number } | null>(null)
const macroFormTarget = ref<CpMacro | null>(null)
const macroFormValues = ref<Record<string, string>>({})

const macroFormFields = computed(() => {
  if (!macroFormTarget.value) return []
  const fields: { key: string; label: string }[] = []
  const seen = new Set<string>()
  for (const action of macroFormTarget.value.actions) {
    if (action.type !== 'send_webhook_with_form' || !action.fields) continue
    for (const f of action.fields) if (!seen.has(f.key)) { seen.add(f.key); fields.push(f) }
  }
  return fields
})
const filteredMacros = computed(() => {
  const q = macroSearch.value.trim().toLowerCase()
  return q ? macrosList.value.filter(m => m.name.toLowerCase().includes(q)) : macrosList.value
})
async function loadMacrosOnce() {
  if (macrosList.value.length) return
  macrosLoading.value = true
  try { macrosList.value = await listMacros(token.value) } finally { macrosLoading.value = false }
}
function toggleMacroPanel() {
  macroPanelOpen.value = !macroPanelOpen.value
  if (macroPanelOpen.value) { loadMacrosOnce(); resetMacroRun() }
}
function resetMacroRun() {
  macroRunning.value = false; macroRunDone.value = false; macroRunningObj.value = null
  macroResult.value = null; macroFormTarget.value = null; macroFormValues.value = {}
}
function runMacroRow(macro: CpMacro) {
  const hasForm = macro.actions.some(a => a.type === 'send_webhook_with_form' && a.fields?.length)
  if (hasForm) { macroFormValues.value = {}; macroFormTarget.value = macro; return }
  executeMacroRow(macro, undefined)
}
function submitMacroForm() {
  if (!macroFormTarget.value) return
  const macro = macroFormTarget.value
  const values = { ...macroFormValues.value }
  macroFormTarget.value = null
  executeMacroRow(macro, values)
}
async function executeMacroRow(macro: CpMacro, formValues: Record<string, string> | undefined) {
  if (!conv.value) return
  macroRunningObj.value = macro
  macroRunning.value = true
  macroRunDone.value = false
  macroResult.value = await executeMacro(token.value, macro.id, conv.value.id, formValues)
  macroRunning.value = false
  macroRunDone.value = true
  fetchMsgs(conv.value.id, true)
}

/* ── Templates ── */
const {
  tplOpen, tplsLoading, tplsApproved, tplSel, tplVarNums, tplVars, tplVarMapping,
  tplSending, tplPreviewHtml, openTplModal, closeTplModal, selectTpl, setTplVar, sendTpl, mappedLabel,
} = useWhatsappTemplatesCp({
  getToken: () => token.value,
  getConversationId: () => conv.value?.id ?? null,
  onSent: () => { if (conv.value) fetchMsgs(conv.value.id, true) },
})

// Precisa vir depois de agentMenuOpen/teamMenuOpen/tagsMenuOpen/macroPanelOpen (usados no
// callback) — com immediate:true o callback roda na hora que watch() é chamado, então
// declarar isso antes dessas consts dispara "Cannot access before initialization" (TDZ).
watch(() => props.conversationId, id => {
  statusMenuOpen.value = false
  agentMenuOpen.value = false
  teamMenuOpen.value = false
  tagsMenuOpen.value = false
  macroPanelOpen.value = false
  draft.value = ''
  files.value = []
  msgRealtimeUnsub?.()
  const offMsg = onConversationMessage(id, message => onRealtimeMsg(id, message))
  const offStatus = onConversationStatus(id, evt => onRealtimeStatus(id, evt))
  // Conexão voltou depois de cair: busca o que chegou na queda e junta com o que já está na tela.
  const offRecovered = onRealtimeRecovered(() => fetchMsgs(id, true))
  msgRealtimeUnsub = () => { offMsg(); offStatus(); offRecovered() }
  loadConversation(id)
  presence.seen(id)
}, { immediate: true })

/** Tique novo (enviada/entregue/lida/falhou) numa mensagem já na tela. */
function onRealtimeStatus(convId: string, evt: MessageStatusNotification) {
  const msgs = msgCache.value[convId]
  if (!msgs?.some(m => m.id === evt.messageId)) return
  msgCache.value = {
    ...msgCache.value,
    [convId]: msgs.map(m => m.id === evt.messageId ? { ...m, deliveryStatus: evt.status, deliveryError: evt.error } : m),
  }
}
function onVisibleAgain() {
  if (document.visibilityState === 'visible') presence.seen(props.conversationId)
}

let nowTimer: ReturnType<typeof setInterval> | null = null
onMounted(() => {
  nowTimer = setInterval(() => { nowSec.value = Math.floor(Date.now() / 1000) }, 30_000)
  document.addEventListener('visibilitychange', onVisibleAgain)
})
onUnmounted(() => {
  if (nowTimer) clearInterval(nowTimer)
  document.removeEventListener('visibilitychange', onVisibleAgain)
  msgRealtimeUnsub?.()
})
</script>

<style scoped>
.copilot-tag { display: inline-block; margin-right: 4px; padding: 0 5px; border-radius: 4px; background: rgba(124,58,237,.14); color: #7c3aed; font-size: 9.5px; font-weight: 700; line-height: 15px; vertical-align: middle; }
.fcp-col { flex: 1; display: flex; flex-direction: column; min-width: 0; background: var(--white); border-left: 1px solid var(--ink-10); }
.fcp-loading, .fcp-empty { flex: 1; display: flex; align-items: center; justify-content: center; color: var(--ink-40); }
.fcp-spinner { width: 24px; height: 24px; border: 3px solid var(--ink-10); border-top-color: var(--red); border-radius: 50%; animation: fcp-spin .8s linear infinite; }
.fcp-spinner-sm { display: inline-block; width: 14px; height: 14px; border: 2px solid var(--ink-10); border-top-color: var(--red); border-radius: 50%; animation: fcp-spin .8s linear infinite; }
@keyframes fcp-spin { to { transform: rotate(360deg); } }

.fcp-head { display: flex; align-items: center; gap: 10px; padding: 12px 16px; border-bottom: 1px solid var(--ink-10); flex-shrink: 0; }
.fcp-av-wrap { position: relative; flex-shrink: 0; }
.fcp-av { width: 38px; height: 38px; border-radius: 50%; display: flex; align-items: center; justify-content: center; color: #fff; font-weight: 700; font-size: 13px; overflow: hidden; }
.fcp-av img { width: 100%; height: 100%; object-fit: cover; }
.fcp-status-dot { position: absolute; bottom: -1px; right: -1px; width: 10px; height: 10px; border-radius: 50%; border: 2px solid var(--white); }
.fcp-status-dot.open { background: var(--blue); }
.fcp-status-dot.pending { background: #d97706; }
.fcp-status-dot.resolved { background: var(--green); }
.fcp-status-dot.closed { background: var(--ink-30); }

.fcp-hinfo { flex: 1; min-width: 0; }
.fcp-hname-row { display: flex; align-items: baseline; gap: 8px; }
.fcp-hname { font-size: 14px; font-weight: 700; color: var(--ink-80); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.fcp-hphone { font-size: 11px; color: var(--ink-40); display: flex; align-items: center; gap: 4px; white-space: nowrap; }
.fcp-phone-copy { background: none; border: none; color: var(--ink-40); cursor: pointer; padding: 2px; display: flex; }
.fcp-phone-copy.copied { color: var(--green); }
.fcp-hsub { display: flex; align-items: center; gap: 6px; font-size: 11px; color: var(--ink-40); margin-top: 2px; flex-wrap: wrap; }
.fcp-tag { padding: 1px 8px; border-radius: 10px; font-size: 10px; font-weight: 700; background: var(--ink-05); color: var(--ink-40); }
.fcp-tag--fluxo { background: rgba(59,130,246,.1); color: #2563eb; }
.fcp-tag--open { background: rgba(3,105,161,.1); color: var(--blue); }
.fcp-tag--pending { background: rgba(217,119,6,.1); color: #d97706; }
.fcp-tag--resolved { background: rgba(31,140,90,.1); color: var(--green); }
.fcp-tag--sla.breached { background: rgba(220,38,38,.1); color: #dc2626; }
.fcp-tag--sla.atRisk { background: rgba(217,119,6,.1); color: #d97706; }

.fcp-icon-btn { width: 28px; height: 28px; border-radius: 50%; border: none; background: var(--ink-05); color: var(--ink-60); display: flex; align-items: center; justify-content: center; cursor: pointer; flex-shrink: 0; }
.fcp-icon-btn:hover { background: var(--ink-10); }
.fcp-icon-btn.active { background: rgba(188,12,36,.1); color: var(--red); }
.fcp-s-dot { width: 9px; height: 9px; border-radius: 50%; }
.fcp-s-dot.open { background: var(--blue); }
.fcp-s-dot.pending { background: #d97706; }
.fcp-s-dot.resolved { background: var(--green); }

.fcp-dd-wrap { position: relative; }
.fcp-dd-menu { position: absolute; top: 100%; right: 0; margin-top: 4px; background: var(--white); border: 1px solid var(--ink-10); border-radius: var(--r-sm); box-shadow: var(--shadow-md); z-index: 20; min-width: 160px; padding: 4px; }
.fcp-dd-menu--wide { min-width: 220px; max-height: 260px; overflow-y: auto; }
.fcp-sopt { display: flex; align-items: center; gap: 8px; width: 100%; padding: 7px 10px; background: none; border: none; font-size: 12.5px; color: var(--ink-70); cursor: pointer; border-radius: 4px; text-align: left; }
.fcp-sopt:hover { background: var(--ink-05); }
.fcp-dd-search { width: 100%; box-sizing: border-box; padding: 6px 8px; border: 1px solid var(--ink-10); border-radius: 4px; font-size: 12px; margin-bottom: 4px; outline: none; }
.fcp-dd-loading { display: flex; justify-content: center; padding: 10px; }
.fcp-dd-list { display: flex; flex-direction: column; }
.fcp-dd-opt { display: flex; align-items: center; gap: 6px; padding: 7px 8px; background: none; border: none; font-size: 12.5px; color: var(--ink-70); cursor: pointer; border-radius: 4px; text-align: left; }
.fcp-dd-opt:hover { background: var(--ink-05); }
.fcp-dd-opt.active { color: var(--red); font-weight: 600; }
.fcp-dd-opt--check { cursor: pointer; }
.fcp-lbl-dot { width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0; }

.fcp-toolbar { display: flex; flex-wrap: wrap; gap: 6px; padding: 8px 16px; border-bottom: 1px solid var(--ink-05); flex-shrink: 0; }
.fcp-pill { display: inline-flex; align-items: center; gap: 4px; padding: 3px 10px; border-radius: 999px; font-size: 11px; font-weight: 600; border: 1px solid var(--ink-10); background: var(--ink-02); color: var(--ink-60); cursor: pointer; white-space: nowrap; }
.fcp-pill--sel { background: rgba(37,99,235,.08); color: #2563eb; border-color: rgba(37,99,235,.2); }
.fcp-pill--add { color: var(--ink-40); border-style: dashed; }
.fcp-pill--tag { cursor: default; }
.fcp-pill-x { background: none; border: none; color: var(--ink-40); cursor: pointer; font-size: 12px; line-height: 1; padding: 0; }

.fcp-window-banner { display: flex; align-items: center; justify-content: center; gap: 7px; padding: 8px 14px; background: #fef2f2; border-top: 1px solid #fecaca; border-bottom: 1px solid #fecaca; font-size: 11.5px; font-weight: 600; color: #b91c1c; flex-shrink: 0; }

.fcp-msgs { flex: 1; overflow-y: auto; padding: 14px 16px; display: flex; flex-direction: column; gap: 8px; background: var(--ink-02); }
.fcp-msgs-loading, .fcp-load-more { display: flex; justify-content: center; padding: 14px; }
.fcp-no-more { text-align: center; font-size: 11px; color: var(--ink-30); padding: 6px; }
.fcp-msg-row { display: flex; align-items: flex-end; gap: 6px; max-width: 78%; }
.fcp-msg-row.out { flex-direction: row-reverse; align-self: flex-end; }
.fcp-msg-row.inn { align-self: flex-start; }
.fcp-msg-row.act { align-self: center; max-width: 90%; }
.fcp-msg-act { font-size: 11px; color: var(--ink-40); font-style: italic; text-align: center; padding: 4px 10px; }
.fcp-msg-av, .fcp-msg-av-spacer { width: 24px; height: 24px; border-radius: 50%; flex-shrink: 0; }
.fcp-msg-av { display: flex; align-items: center; justify-content: center; color: #fff; font-size: 10px; font-weight: 700; overflow: hidden; }
.fcp-msg-av img { width: 100%; height: 100%; object-fit: cover; }
.fcp-bubble { background: #fff; border-radius: 12px; padding: 8px 12px; box-shadow: 0 1px 3px rgba(0,0,0,.07); position: relative; }
.fcp-msg-row.out .fcp-bubble { background: #BC0C24; color: #fff; border-bottom-right-radius: 3px; }
.fcp-msg-row.inn .fcp-bubble { border-bottom-left-radius: 3px; }
.fcp-bubble.priv { background: #fef9c3; color: #713f12; }
.fcp-priv-tag { display: block; font-size: 10px; font-weight: 700; margin-bottom: 3px; opacity: .7; }
.fcp-bubble-text { font-size: 13px; white-space: pre-wrap; overflow-wrap: anywhere; }
.fcp-bubble-text :deep(.wa-mono) { font-family: ui-monospace, Consolas, monospace; font-size: .92em; padding: 0 3px; border-radius: 3px; background: rgba(0,0,0,.06); }
.fcp-bubble-ts { display: block; font-size: 9.5px; opacity: .65; margin-top: 3px; text-align: right; white-space: nowrap; }
.fcp-att { margin-top: 6px; }
.fcp-att-img { max-width: 220px; border-radius: 8px; display: block; }
.fcp-att-audio, .fcp-att-video { max-width: 240px; }
.fcp-att-file { font-size: 12px; color: inherit; }

.fcp-files { display: flex; flex-wrap: wrap; gap: 6px; padding: 8px 16px 0; }
.fcp-file-pill { display: flex; align-items: center; gap: 6px; background: var(--ink-05); border-radius: 14px; padding: 4px 10px; font-size: 11px; }
.fcp-file-rm { background: none; border: none; color: var(--ink-40); cursor: pointer; }

.fcp-composer { display: flex; align-items: flex-end; gap: 8px; padding: 10px 16px; border-top: 1px solid var(--ink-10); flex-shrink: 0; }
.fcp-composer.priv { background: #fffbeb; }
.fcp-finished { display: flex; align-items: center; gap: 9px; padding: 12px 16px; border-top: 1px solid var(--ink-10); background: #f9fafb; color: var(--ink-60); font-size: 13px; line-height: 1.4; flex-shrink: 0; }
.fcp-finished svg { flex-shrink: 0; opacity: .6; }
.fcp-finished span { flex: 1; min-width: 0; }
.fcp-finished-btn { flex-shrink: 0; padding: 6px 13px; border-radius: 8px; border: 1px solid #BC0C24; background: #fff; color: #BC0C24; font: inherit; font-size: 12.5px; font-weight: 600; cursor: pointer; }
.fcp-finished-btn:hover { background: #BC0C24; color: #fff; }
.fcp-attach-btn { display: flex; align-items: center; justify-content: center; width: 32px; height: 32px; border-radius: 50%; background: var(--ink-05); color: var(--ink-50); cursor: pointer; flex-shrink: 0; }
.fcp-attach-btn.disabled { opacity: .4; cursor: not-allowed; }
.fcp-priv-btn { width: 32px; height: 32px; border-radius: 50%; border: none; background: var(--ink-05); color: var(--ink-50); cursor: pointer; flex-shrink: 0; display: flex; align-items: center; justify-content: center; }
.fcp-priv-btn.active { background: #fde68a; color: #713f12; }
.fcp-ta { flex: 1; resize: none; border: 1px solid var(--ink-10); border-radius: 18px; padding: 8px 14px; font-size: 13px; font-family: inherit; outline: none; max-height: 120px; }
.fcp-ta:focus { border-color: var(--red); }
.fcp-send-btn, .fcp-tpl-btn { width: 34px; height: 34px; border-radius: 50%; border: none; background: var(--red); color: #fff; cursor: pointer; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.fcp-tpl-btn { width: auto; border-radius: 16px; padding: 0 14px; font-size: 12px; font-weight: 600; }
.fcp-send-btn:disabled { opacity: .4; cursor: not-allowed; }

.fcp-macro-panel { flex: 1; overflow-y: auto; padding: 14px 16px; display: flex; flex-direction: column; gap: 10px; }
.fcp-macro-head { display: flex; align-items: center; gap: 8px; }
.fcp-macro-back { background: none; border: none; color: var(--ink-40); cursor: pointer; font-size: 12px; }
.fcp-macro-title { font-size: 13px; font-weight: 700; color: var(--ink-80); }
.fcp-macro-search { flex: 1; padding: 7px 10px; border: 1px solid var(--ink-10); border-radius: 8px; font-size: 12.5px; outline: none; }
.fcp-macro-count { font-size: 11px; color: var(--ink-40); }
.fcp-macro-list { display: flex; flex-direction: column; gap: 6px; }
.fcp-macro-item { display: flex; flex-direction: column; align-items: flex-start; gap: 2px; padding: 9px 12px; background: var(--white); border: 1px solid var(--ink-10); border-radius: 8px; cursor: pointer; text-align: left; }
.fcp-macro-item:hover { border-color: var(--red); }
.fcp-macro-item-name { font-size: 12.5px; font-weight: 600; color: var(--ink-80); }
.fcp-macro-item-sub { font-size: 10.5px; color: var(--ink-40); }
.fcp-macro-empty { text-align: center; color: var(--ink-40); font-size: 12px; padding: 16px; }
.fcp-macro-summary { padding: 10px 14px; border-radius: 8px; font-size: 12.5px; font-weight: 600; }
.fcp-macro-summary.ok { background: rgba(31,140,90,.1); color: var(--green); }
.fcp-macro-summary.warn { background: rgba(220,38,38,.1); color: #dc2626; }
.fcp-macro-form { display: flex; flex-direction: column; gap: 10px; }
.fcp-mform-hint { font-size: 11.5px; color: var(--ink-40); }
.fcp-mform-field { display: flex; flex-direction: column; gap: 4px; }
.fcp-mform-label { font-size: 11px; font-weight: 600; color: var(--ink-50); }
.fcp-mform-input { padding: 7px 10px; border: 1px solid var(--ink-10); border-radius: 6px; font-size: 12.5px; outline: none; }
.fcp-mform-mapped { font-size: 11px; color: var(--green); font-style: italic; }
.fcp-btn-primary { padding: 9px 16px; border-radius: 8px; background: var(--red); color: #fff; border: none; font-size: 13px; font-weight: 600; cursor: pointer; }
.fcp-btn-primary:disabled { opacity: .5; cursor: not-allowed; }

.fcp-tpl-overlay { position: fixed; inset: 0; background: rgba(2,22,39,.45); display: flex; align-items: center; justify-content: center; z-index: 1000; }
.fcp-tpl-dialog { background: var(--white); border-radius: 10px; width: 100%; max-width: 720px; max-height: 85vh; display: flex; flex-direction: column; overflow: hidden; }
.fcp-tpl-head { display: flex; align-items: center; justify-content: space-between; padding: 14px 18px; border-bottom: 1px solid var(--ink-10); font-size: 14px; font-weight: 700; color: var(--ink-80); }
.fcp-tpl-body { flex: 1; display: flex; overflow: hidden; }
.fcp-tpl-list-col { width: 220px; border-right: 1px solid var(--ink-10); overflow-y: auto; padding: 10px; display: flex; flex-direction: column; gap: 4px; }
.fcp-tpl-item { text-align: left; padding: 8px 10px; border-radius: 6px; border: none; background: none; font-size: 12.5px; color: var(--ink-70); cursor: pointer; }
.fcp-tpl-item:hover { background: var(--ink-05); }
.fcp-tpl-item.active { background: rgba(188,12,36,.08); color: var(--red); font-weight: 600; }
.fcp-tpl-config-col { flex: 1; padding: 16px; overflow-y: auto; display: flex; flex-direction: column; gap: 12px; }
.fcp-tpl-preview { background: var(--ink-02); border-radius: 8px; padding: 12px 14px; font-size: 13px; white-space: pre-wrap; }

/* Celular (MobileView): tela cheia, toque maior, modal de template empilhado */
.fcp-col--mobile { height: 100%; }
.fcp-col--mobile .fcp-head { padding: calc(10px + env(safe-area-inset-top, 0px)) 12px 10px; gap: 8px; }
.fcp-col--mobile .fcp-hphone { display: none; }
.fcp-col--mobile .fcp-icon-btn { width: 34px; height: 34px; }
.fcp-col--mobile .fcp-toolbar { padding: 8px 12px; overflow-x: auto; flex-wrap: nowrap; }
.fcp-col--mobile .fcp-dd-menu { position: fixed; left: 12px; right: 12px; top: auto; margin-top: 4px; min-width: 0; max-height: 50vh; overflow-y: auto; }
.fcp-col--mobile .fcp-msgs { padding: 12px; }
.fcp-col--mobile .fcp-msg-row { max-width: 88%; }
.fcp-col--mobile .fcp-att-img, .fcp-col--mobile .fcp-att-audio, .fcp-col--mobile .fcp-att-video { max-width: 100%; }
.fcp-col--mobile .fcp-composer { padding: 8px 10px calc(8px + env(safe-area-inset-bottom, 0px)); }
.fcp-col--mobile .fcp-ta { font-size: 16px; }
.fcp-col--mobile .fcp-empty { flex-direction: column; gap: 12px; }
@media (max-width: 640px) {
  .fcp-tpl-dialog { max-height: 92vh; margin: 0 8px; }
  .fcp-tpl-body { flex-direction: column; overflow-y: auto; }
  .fcp-tpl-list-col { width: auto; max-height: 30vh; border-right: none; border-bottom: 1px solid var(--ink-10); }
}
</style>
